// ============================================
// FILE: src/components/HomeScreen.jsx
// ============================================

import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Bell, MapPin, Heart, Gift, Package, Plus, ShoppingCart, LogOut, Flame, ChevronRight } from 'lucide-react';

import LocationModal from './modals/LocationModal';
import ProductDetailsModal from './modals/ProductDetailsModal';
import { locations, subscriptionPlans, categoryIcons } from '../data/products';
import { getProductImage, getProductEmoji, resolveProductImageUrl } from '../utils/imageHelpers';
import { getFinalPrice, hasVariants, getVariantPriceFrom } from '../utils/priceHelpers';
import { isCategoryVisible } from '../utils/categoryConfig';
import CategoryBannerCarousel from './CategoryBannerCarousel';
import CategoryShelves from './CategoryShelves';
import QuantityTextBox from './QuantityTextBox';
import { usesQuantityTextBox } from '../utils/quantityHelpers';

const HomeScreen = ({
  searchQuery = '',
  setSearchQuery,
  setShowNotifications,
  setShowLocationModal,
  showLocationModal,
  selectedLocation,
  setSelectedLocation,
  addToCart,
  setShowSubscription,
  products,
  filteredProducts,
  cart = [],
  getCartCount,
  getCartTotal,
  updateQuantity,
  openCheckout,
  userProfile = {},
  notifications = [],
  favorites = [],
  setShowFavorites,
  setFavorites,
  toggleFavorite,
  setActiveTab,
  selectedCategory = 'All',
  setSelectedCategory,
  categories: apiCategories = [],
  onCategoryClick,
  onOpenOffers,
  showHeader = true,
  showToast,
  appSettings,
  handleLogout,
  featureSections = [],
  appTheme = 'blue',
}) => {
  const favArray = Array.isArray(favorites) ? favorites : [];
  const logoSrc = `${process.env.PUBLIC_URL || ''}/logo.png`;
  const isProductFavorited = (productId) => (
    favArray.some((item) => String(item?.id) === String(productId))
  );
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductDetails, setShowProductDetails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [quantitySelections, setQuantitySelections] = useState({});
  const [quantityLoading, setQuantityLoading] = useState({});
  const productsPerPage = 40;

  // Ref for the fixed header — used to measure height so content isn't hidden behind it
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    const measure = () => {
      if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // Ref for the products section — used to scroll into view after search
  const productsSectionRef = useRef(null);

  // Scroll to product list when the user starts typing a search query
  useEffect(() => {
    if (searchQuery && searchQuery.length > 0) {
      const timer = setTimeout(() => {
        productsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
      return () => clearTimeout(timer);
    }
  }, [searchQuery]);

  // Banner rotation now lives inside CategoryBannerCarousel, which owns the
  // slide set and has to restart the timer on manual navigation.

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory]);

  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const endIndex = startIndex + productsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, endIndex);

  // "All" with no search: one shelf per category, each with its own banners.
  const showCategoryShelves = selectedCategory === 'All' && !searchQuery && apiCategories.length > 0;

  const categories = apiCategories.length > 0
    ? ['All', ...apiCategories.map(c => c.name).filter(isCategoryVisible)]
    : ['All', ...new Set(products.map(p =>
        typeof p.category === 'object' && p.category?.name ? p.category.name : p.category || 'Uncategorized'
      ).filter(Boolean).filter(isCategoryVisible))];

  const getWeightParts = (product) => {
    if (!product) return { value: null, unit: '' };
    if (product.weight !== undefined && product.weight !== null && product.weight !== '') {
      const raw = String(product.weight).trim();
      const match = raw.match(/^(\d*\.?\d+)\s*([a-zA-Z]*)$/);
      if (match) return { value: parseFloat(match[1]), unit: match[2] || product.unit || '' };
    }
    return { value: null, unit: product.unit || '' };
  };

  const isWeightUnit = (unit) => {
    if (!unit) return false;
    return ['kg'].includes(String(unit).toLowerCase().trim());
  };

  const formatUnit = (unit) => {
    if (!unit) return '';
    const str = String(unit).trim();
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const getQuantityOptions = (product) => {
    const { unit } = getWeightParts(product);
    return isWeightUnit(unit) ? [0.5, 1, 2, 3] : [1, 2, 3, 4];
  };

  const formatWeightLabel = (product) => {
    const { value, unit } = getWeightParts(product);
    if (value) return `${value}${unit ? ` ${unit}` : ''}`;
    return product.weight ? String(product.weight) : '';
  };

  const getSelectedQuantity = (productId) => quantitySelections[productId] || 1;

  const setSelectedQuantity = (productId, qty) => {
    setQuantitySelections((prev) => ({ ...prev, [productId]: qty }));
  };

  const handleAddToCartWithQuantity = (product) => {
    addToCart(product, getSelectedQuantity(product.id));
  };

  // One product card, shared by the flat grid and the per-category shelves.
  const renderProductCard = (product, index = 0) => {
    const isFav = isProductFavorited(product.id);
    const hasDiscount = Number(product.sale_price) > 0 && Number(product.price) > 0 && Number(product.sale_price) < Number(product.price);
    const offPercent = hasDiscount
      ? Math.round(((Number(product.price) - Number(product.sale_price)) / Number(product.price)) * 100)
      : 0;
    return (
      <div key={product.id} className="rounded overflow-hidden relative product-card glass-card"
        style={{ animationDelay: `${(index % 10) * 50}ms` }}>
        <div
          className="relative overflow-hidden h-44 cursor-pointer glass-image group"
          onClick={() => { setSelectedProduct(product); setShowProductDetails(true); }}
        >
          {getProductImage(product, 'card')}
          {hasDiscount && (
            <span className="absolute top-3 left-3 text-xs bg-[#ff3f6c] text-white px-2 py-0.5 rounded-full font-semibold shadow-sm ring-1 ring-white/70 badge-off">
              {offPercent}% OFF
            </span>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); if (typeof toggleFavorite === 'function') toggleFavorite(product); }}
            className="absolute top-3 right-3 p-2 bg-white rounded-full shadow hover:scale-105 transition-transform"
          >
            <Heart size={18} className={isFav ? 'text-pink-500' : 'text-[#94969f]'} fill={isFav ? 'currentColor' : 'none'} />
          </button>
        </div>

        <div className="p-3">
          <h3
            className="font-display font-semibold text-[#282c3f] text-sm mb-1 cursor-pointer hover:text-[#ff3f6c] transition-colors"
            onClick={() => { setSelectedProduct(product); setShowProductDetails(true); }}
          >{product.name}</h3>
          <div className="flex items-center gap-2 mb-2">
            {product.weight && (
              <span className="text-xs bg-gray-100 text-[#535766] px-2 py-1 rounded">{formatWeightLabel(product)}</span>
            )}
            {!product.weight && product.fat && (
              <span className="text-xs bg-[#e8f7f3] text-[#03a685] px-2 py-1 rounded">{product.fat} fat</span>
            )}
            {!product.weight && !product.fat && product.brand && (
              <span className="text-xs bg-gray-100 text-[#535766] px-2 py-1 rounded">{product.brand}</span>
            )}
          </div>
          <div className="flex items-center gap-2 mb-2">
            {hasVariants(product) ? (
              <span className="text-sm font-extrabold text-emerald-600 drop-shadow-sm">from ₹{getVariantPriceFrom(product)}</span>
            ) : product.sale_price && product.sale_price < product.price ? (
              <>
                <span className="text-sm font-extrabold text-[#03a685] drop-shadow-sm">₹{product.sale_price}</span>
                <span className="text-xs text-[#94969f] line-through">₹{product.price}</span>
              </>
            ) : (
              <span className="text-sm font-extrabold text-emerald-600 drop-shadow-sm">₹{getFinalPrice(product)}</span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              {usesQuantityTextBox(product) ? (
                <QuantityTextBox
                  value={getSelectedQuantity(product.id)}
                  onChange={(qty) => setSelectedQuantity(product.id, qty)}
                />
              ) : (
                getQuantityOptions(product).map((qty) => (
                  <button
                    key={qty}
                    type="button"
                    onClick={() => setSelectedQuantity(product.id, qty)}
                    className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                      getSelectedQuantity(product.id) === qty
                        ? 'bg-[#ff3f6c] text-white border-[#ff3f6c]'
                        : 'bg-white text-[#535766] border-[#eaeaec] hover:border-[#ff3f6c]'
                    }`}
                  >
                    {qty}
                  </button>
                ))
              )}
              {isWeightUnit(getWeightParts(product).unit) && (
                <span className="text-xs text-[#535766] ml-1 font-medium">Kg</span>
              )}
              {!isWeightUnit(getWeightParts(product).unit) && getWeightParts(product).unit && (
                <span className="text-xs text-[#535766] ml-1 font-medium">{formatUnit(getWeightParts(product).unit)}</span>
              )}
            </div>

            {(() => {
              const cartItem = cart.find(item => item.id === product.id);
              if (cartItem && cartItem.quantity > 0) {
                return (
                  <div className="w-full flex items-center justify-center gap-2 bg-[#fff8fa] border border-[#ff3f6c] rounded-lg px-2 py-1 mt-1">
                    <button
                      className="flex items-center justify-center w-8 h-8 rounded-full bg-white border border-[#ff3f6c] text-[#ff3f6c] text-xl font-bold shadow-sm hover:bg-[#fff8fa] focus:outline-none disabled:opacity-50"
                      disabled={quantityLoading[product.id] || cartItem.quantity <= 1}
                      onClick={async () => {
                        if (typeof updateQuantity === 'function' && cartItem.quantity > 1) {
                          setQuantityLoading(prev => ({ ...prev, [product.id]: true }));
                          await updateQuantity(product.id, -1);
                          setQuantityLoading(prev => ({ ...prev, [product.id]: false }));
                        }
                      }}
                    >
                      <span>&#8722;</span>
                    </button>
                    <span className="text-base font-bold text-[#ff3f6c] w-10 text-center select-none">
                      {quantityLoading[product.id] ? '...' : cartItem.quantity}
                    </span>
                    <button
                      className="flex items-center justify-center w-8 h-8 rounded-full bg-white border border-[#ff3f6c] text-[#ff3f6c] text-xl font-bold shadow-sm hover:bg-[#fff8fa] focus:outline-none disabled:opacity-50"
                      disabled={!!quantityLoading[product.id]}
                      onClick={async () => {
                        if (typeof updateQuantity === 'function' && !quantityLoading[product.id]) {
                          setQuantityLoading(prev => ({ ...prev, [product.id]: true }));
                          await updateQuantity(product.id, 1);
                          setQuantityLoading(prev => ({ ...prev, [product.id]: false }));
                        }
                      }}
                    >
                      <span>&#43;</span>
                    </button>
                  </div>
                );
              }
              return (
                <button
                  onClick={() => handleAddToCartWithQuantity(product)}
                  className="w-full bg-[#ff3f6c] text-white py-2 rounded-lg text-sm font-medium hover:bg-[#e03a60] transition-colors flex items-center justify-center gap-1"
                >
                  <Plus size={16} />
                  Add to Cart
                </button>
              );
            })()}
          </div>

          {Boolean(Number(product.is_subscribable)) && (
            <button
              onClick={() => setShowSubscription(product)}
              className="w-full bg-[#03a685] text-white py-2 rounded-lg text-sm font-medium hover:bg-[#028f73] transition-colors mt-2"
            >
              Subscribe &amp; Save
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="pb-20" style={{ background: '#f7f7f7', color: '#1b1b1b', minHeight: '100vh' }}>

      {/* ── Fixed header — location + search ── */}
      {showHeader && (
        <div
          ref={headerRef}
          className="fixed top-0 bg-white left-0 right-0 max-w-md mx-auto border-t border-[#eaeaec] bg-white z-[210]"
          style={{
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            paddingTop: 'env(safe-area-inset-top, 0px)',
          }}
        >

        {/* Top bar */}
        <div className="flex items-center justify-between px-4 pt-3 pb-2" style={{ borderBottom: '1px solid #f0f0f0' }}>
          <button onClick={() => setShowLocationModal(true)} className="flex items-center gap-1.5 min-w-0">
            <MapPin size={16} style={{ color: '#ff3f6c', flexShrink: 0 }} />
            <span className="text-sm font-semibold text-[#282c3f] truncate max-w-[160px]">{selectedLocation}</span>
            <span className="text-[#94969f] text-xs">▾</span>
          </button>

          <div className="flex items-center gap-1 flex-shrink-0">
            <button onClick={() => setShowNotifications(true)} className="relative p-2 rounded-full hover:bg-gray-100 transition-all">
              <Bell size={20} className="text-[#535766]" />
              {notifications.filter(n => n.unread).length > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 rounded-full border border-white flex items-center justify-center">
                  <span className="text-[9px] font-bold text-white">{notifications.filter(n => n.unread).length}</span>
                </span>
              )}
            </button>
            <button onClick={() => setShowFavorites(true)} className="relative p-2 rounded-full hover:bg-gray-100 transition-all">
              <Heart size={20} className="text-[#535766]" />
              {favArray.length > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-pink-500 rounded-full border border-white flex items-center justify-center">
                  <span className="text-[9px] font-bold text-white">{favArray.length}</span>
                </span>
              )}
            </button>
            {handleLogout && (
              <button onClick={handleLogout} className="p-2 rounded-full hover:bg-gray-100 transition-all" title="Logout">
                <LogOut size={20} className="text-[#535766]" />
              </button>
            )}
          </div>
        </div>

        {/* Brand + search */}
        <div className="px-4 pt-3 pb-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded flex items-center justify-center flex-shrink-0 shadow-sm" style={{ background: '#f3f4f6' }}>
              <img src={logoSrc} alt={appSettings?.appName || 'logo'} className="w-8 h-8 rounded object-contain" />
            </div>
            <div className="min-w-0">
              <h1 className="app-brand-title truncate" style={{ color: '#1b1b1b' }}>
                {appSettings?.appName || 'DealStore'}
              </h1>
              {userProfile?.name
                ? <p className="text-xs text-[#94969f]">Hey <span className="font-semibold text-[#535766]">{userProfile.name}</span> 👋</p>
                : <p className="text-xs text-[#94969f]">Fresh picks &amp; daily savings</p>
              }
            </div>
          </div>

          <div className="flex items-center gap-3 px-4 py-3 rounded" style={{ background: '#f2f2f2', border: '1.5px solid #e8e8e8' }}>
            <Search size={18} className="text-[#94969f] flex-shrink-0" />
            <input
              type="text"
              placeholder="Search for products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent outline-none text-sm text-[#282c3f] placeholder-gray-400"
            />
            {searchQuery.length > 0 && (
              <button
                onClick={() => setSearchQuery('')}
                className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-300 hover:bg-gray-400 flex items-center justify-center transition-colors"
                aria-label="Clear search"
              >
                <X size={12} className="text-[#535766]" />
              </button>
            )}
          </div>
        </div>
      </div>
      )}

      {/* Spacer — keeps content from hiding behind the fixed header */}
      <div style={{ height: showHeader ? (headerHeight || 120) : 0 }} aria-hidden="true" />

      {/* Quick action pills */}
      <div className="flex gap-2 px-4 py-3 overflow-x-auto hide-scrollbar bg-white" style={{ borderBottom: '1px solid #f0f0f0' }}>
        {[
          { icon: <Gift size={14} />, label: 'Offers', action: () => onOpenOffers ? onOpenOffers('all') : document.querySelector('.banner-carousel')?.scrollIntoView({ behavior: 'smooth' }) },
          { icon: <Package size={14} />, label: 'Track Order', action: () => setActiveTab && setActiveTab('orders') },
          { icon: <span className="text-xs">🔔</span>, label: 'Subscribe', action: () => setShowSubscription(true) },
        ].map(({ icon, label, action }) => (
          <button key={label} onClick={action}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium text-[#535766] transition-all hover:bg-[#fff8fa]"
            style={{ background: '#fff', border: '1.5px solid #e8e8e8' }}
          >
            <span style={{ color: '#ff3f6c' }}>{icon}</span>
            {label}
          </button>
        ))}
      </div>

      {/* ── UC-style Category Bar ── */}
      <div className="bg-white mb-3" style={{ borderBottom: '1px solid #f0f0f0' }}>
        <div className="flex items-center justify-between px-4 pt-3 pb-1">
          <h2 className="text-sm font-bold text-[#282c3f]">Shop by Category</h2>
          <span className="text-xs text-[#94969f]">{categories.length - 1} types</span>
        </div>
        <div className="flex gap-3 overflow-x-auto px-4 py-3 hide-scrollbar">
          {categories.map(category => {
            const isActive = selectedCategory === category;
            const catPalettes = ['#FF6B6B','#4ECDC4','#45B7D1','#96CEB4','#FFEAA7','#DDA0DD','#98D8C8','#F7DC6F','#BB8FCE','#85C1E9'];
            const idx = categories.indexOf(category);
            const circleColor = isActive ? '#ff3f6c' : catPalettes[idx % catPalettes.length];
            const catObj = category !== 'All' ? apiCategories.find(c => c.name === category) : null;
            const catImgUrl = catObj ? resolveProductImageUrl(catObj.image) : null;
            return (
              <button
                key={category}
                onClick={() => {
                  if (category === 'All') {
                    setSelectedCategory && setSelectedCategory('All');
                  } else if (catObj && onCategoryClick) {
                    onCategoryClick(catObj);
                  } else {
                    setSelectedCategory && setSelectedCategory(category);
                  }
                }}
                className="flex flex-col items-center gap-1.5 flex-shrink-0 transition-all"
                style={{ minWidth: 64 }}
              >
                <div className="w-14 h-14 rounded-full flex items-center justify-center shadow-sm transition-all relative overflow-hidden"
                  style={{
                    background: isActive ? 'linear-gradient(135deg,#ff3f6c,#e03a60)' : `${circleColor}22`,
                    border: isActive ? '2.5px solid #ff3f6c' : `2px solid ${circleColor}55`,
                    transform: isActive ? 'scale(1.08)' : 'scale(1)',
                  }}>
                  {catImgUrl && (
                    <img
                      src={catImgUrl}
                      alt={category}
                      className="absolute inset-0 w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  {category === 'All'
                    ? <ShoppingCart size={22} style={{ color: isActive ? '#fff' : circleColor }} strokeWidth={2} />
                    : <span className="text-2xl leading-none">{categoryIcons[category] || '📦'}</span>
                  }
                </div>
                <span className="text-[11px] font-semibold text-center leading-tight"
                  style={{ color: isActive ? '#ff3f6c' : '#555', maxWidth: 64 }}>
                  {category}
                </span>
                {isActive && <span className="w-1.5 h-1.5 rounded-full bg-[#ff3f6c]" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Hero carousel — banners come from the admin banners table and are
          scoped to the selected category, falling back to global creative. */}
      <CategoryBannerCarousel
        categories={apiCategories}
        selectedCategory={selectedCategory}
        onCategoryClick={onCategoryClick}
        setSelectedCategory={setSelectedCategory}
        isDesktop={false}
      />

      {/* Feature Sections */}
      {Array.isArray(featureSections) && featureSections.map(section => {
        const sectionColor = section.color || '#3b82f6';
        return (
          <div key={section.id} className="mb-7">
            <div className="mx-4 mb-3 rounded overflow-hidden shadow-sm">
              <div
                className="flex items-center justify-between px-4 py-3"
                style={{ background: `linear-gradient(135deg, ${sectionColor}22 0%, ${sectionColor}08 100%)`, borderLeft: `4px solid ${sectionColor}` }}
              >
                <div className="flex items-center gap-2.5">
                  <div className="flex items-center justify-center w-9 h-9 rounded text-white shadow-md"
                    style={{ background: `linear-gradient(135deg, ${sectionColor}, ${sectionColor}99)` }}>
                    {section.icon ? <i className={`${section.icon} text-sm`} /> : <Flame size={16} />}
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-[#282c3f] leading-tight">{section.name}</h2>
                    <p className="text-[10px] text-[#94969f]">{(section.products || []).length} items available</p>
                  </div>
                  <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold text-white ml-1"
                    style={{ backgroundColor: sectionColor }}>
                    <span className="w-1.5 h-1.5 rounded-full bg-white/80 motion-safe:animate-pulse inline-block" />
                    LIVE
                  </span>
                </div>
                <button
                  onClick={() => onOpenOffers && onOpenOffers(section.id)}
                  className="flex items-center gap-0.5 text-xs font-semibold active:scale-95 transition-transform"
                  style={{ color: sectionColor }}
                >
                  All <ChevronRight size={14} />
                </button>
              </div>
            </div>

            <div className="flex gap-3 overflow-x-auto px-4 pb-3 hide-scrollbar">
              {(section.products || []).map(product => {
                const isFav = isProductFavorited(product.id);
                const hasDiscount = Number(product.sale_price) > 0 && Number(product.price) > 0 && Number(product.sale_price) < Number(product.price);
                const offPercent = hasDiscount
                  ? Math.round(((Number(product.price) - Number(product.sale_price)) / Number(product.price)) * 100)
                  : 0;
                return (
                  <div key={product.id} className="min-w-[148px] max-w-[148px] rounded overflow-hidden flex-shrink-0 bg-white shadow-md"
                    style={{ borderTop: `3px solid ${sectionColor}` }}>
                    <div className="relative h-32 overflow-hidden cursor-pointer group"
                      onClick={() => { setSelectedProduct(product); setShowProductDetails(true); }}>
                      {getProductImage(product, 'card')}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none" />
                      {hasDiscount && (
                        <span className="absolute top-2 left-2 text-[10px] font-bold text-white px-2 py-0.5 rounded-full shadow-md"
                          style={{ background: 'linear-gradient(90deg,#f59e0b,#ef4444)' }}>
                          {offPercent}% OFF
                        </span>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); if (typeof toggleFavorite === 'function') toggleFavorite(product); }}
                        className="absolute top-2 right-2 p-1.5 bg-white/90 backdrop-blur-sm rounded-full shadow hover:scale-110 transition-transform"
                      >
                        <Heart size={12} className={isFav ? 'text-pink-500' : 'text-[#94969f]'} fill={isFav ? 'currentColor' : 'none'} />
                      </button>
                    </div>
                    <div className="p-2.5">
                      <h3 className="text-xs font-semibold text-[#282c3f] mb-1.5 line-clamp-2 cursor-pointer hover:text-[#ff3f6c] leading-tight"
                        onClick={() => { setSelectedProduct(product); setShowProductDetails(true); }}>
                        {product.name}
                      </h3>
                      <div className="flex items-center gap-1.5 mb-2">
                        {hasVariants(product) ? (
                          <span className="text-sm font-bold text-[#282c3f]">from ₹{getVariantPriceFrom(product)}</span>
                        ) : hasDiscount ? (
                          <>
                            <span className="text-sm font-bold text-[#282c3f]">₹{product.sale_price}</span>
                            <span className="text-[10px] text-[#94969f] line-through">₹{product.price}</span>
                          </>
                        ) : (
                          <span className="text-sm font-bold text-[#282c3f]">₹{getFinalPrice(product)}</span>
                        )}
                      </div>
                      <button
                        onClick={() => addToCart(product, 1)}
                        className="w-full text-[11px] text-white py-1.5 rounded font-bold transition-all hover:opacity-90 active:scale-95 flex items-center justify-center gap-1"
                        style={{ background: `linear-gradient(90deg, ${sectionColor}, ${sectionColor}cc)` }}
                      >
                        <Plus size={11} /> Add
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Products — grouped into per-category shelves on the "All" view, a
          flat paginated grid for a selected category or a search. */}
      {(() => {
      const productsGrid = (
      <div className="p-4 products-section" ref={productsSectionRef}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-[#282c3f] font-display">
            {selectedCategory === 'All' ? 'Our Products' : selectedCategory}
          </h2>
          <span className="text-sm text-[#94969f]">
            {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {paginatedProducts.map((product, index) => renderProductCard(product, index))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6 mb-4">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${currentPage === 1 ? 'bg-gray-200 text-[#94969f] cursor-not-allowed' : 'bg-[#ff3f6c] text-white hover:bg-[#e03a60]'}`}
            >
              Previous
            </button>
            <div className="flex gap-2">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-10 h-10 rounded-lg font-medium transition-all ${currentPage === page ? 'bg-[#ff3f6c] text-white shadow-lg' : 'bg-white text-[#535766] hover:bg-gray-100'}`}
                >
                  {page}
                </button>
              ))}
            </div>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${currentPage === totalPages ? 'bg-gray-200 text-[#94969f] cursor-not-allowed' : 'bg-[#ff3f6c] text-white hover:bg-[#e03a60]'}`}
            >
              Next
            </button>
          </div>
        )}
        {totalPages > 1 && (
          <p className="text-center text-sm text-[#94969f] mb-4">
            Showing {startIndex + 1}–{Math.min(endIndex, filteredProducts.length)} of {filteredProducts.length} products
          </p>
        )}
      </div>
      );
      return showCategoryShelves ? (
        <CategoryShelves
          categories={apiCategories}
          products={filteredProducts}
          onCategoryClick={onCategoryClick}
          setSelectedCategory={setSelectedCategory}
          renderProductCard={renderProductCard}
          isDesktop={false}
          fallback={productsGrid}
        />
      ) : productsGrid;
      })()}

      {/* Floating Checkout */}
      {cart.length > 0 && getCartCount() > 0 && (
        <button
          onClick={openCheckout}
          className="fixed bottom-24 right-6 bg-[#ff3f6c] text-white px-6 py-4 rounded-full shadow-2xl hover:shadow-green-500/50 transition-all transform hover:scale-105 flex items-center gap-3 z-50"
        >
          <ShoppingCart size={24} />
          <div className="flex flex-col items-start">
            <span className="text-xs font-medium">Checkout Now</span>
            <span className="text-lg font-bold">{getCartCount()} {getCartCount() === 1 ? 'item' : 'items'}</span>
            <span className="text-xs font-semibold mt-1 text-green-100 bg-green-700 bg-opacity-70 px-2 py-0.5 rounded">
              Total: ₹{typeof getCartTotal === 'function' ? getCartTotal() : cart.reduce((sum, item) => {
                const p = (item.sale_price && item.sale_price < item.price) ? item.sale_price : item.price;
                return sum + (p * item.quantity);
              }, 0)}
            </span>
          </div>
        </button>
      )}

      {/* Product Details Modal */}
      <ProductDetailsModal
        cart={cart}
        getCartCount={getCartCount}
        getCartTotal={getCartTotal}
        openCheckout={openCheckout}
        isOpen={showProductDetails}
        onClose={() => { setShowProductDetails(false); setSelectedProduct(null); }}
        product={selectedProduct}
        addToCart={addToCart}
        showToast={showToast}
        favorites={favorites}
        toggleFavorite={toggleFavorite}
        setShowSubscription={setShowSubscription}
        appTheme={appTheme}
      />
    </div>
  );
};

export default HomeScreen;
